import { createBlobRef, approximateJsonBytes, assertBlobIntegrity } from './bytes.js';
import { StorageError, mapStorageError } from './errors.js';
import { pendingMigrations } from './migrations.js';
import { checkExpectedVersion, createDeletedDocument, createPutDocument, defaultClock, defaultIdSource, documentKey, operationInputForDocument, } from './model.js';
import { assertBlobRef, assertJsonValue, assertOutboxOperation, assertStoredDocument, normalizeQueryWindow, } from './validation.js';
function initialState() {
    return {
        documents: new Map(),
        outbox: new Map(),
        operationSequences: new Map(),
        blobs: new Map(),
        nextSequence: 1,
        schemaVersion: 0,
    };
}
function cloneState(source) {
    return {
        documents: new Map([...source.documents].map(([key, value]) => [key, structuredClone(value)])),
        outbox: new Map([...source.outbox].map(([key, value]) => [key, structuredClone(value)])),
        operationSequences: new Map(source.operationSequences),
        blobs: new Map([...source.blobs].map(([key, value]) => [key, {
                ref: structuredClone(value.ref),
                blob: value.blob,
            }])),
        nextSequence: source.nextSequence,
        schemaVersion: source.schemaVersion,
    };
}
function stateUsageBytes(state) {
    const logical = {
        documents: [...state.documents.values()],
        outbox: [...state.outbox.values()],
        blobRefs: [...state.blobs.values()].map(({ ref }) => ref),
        schemaVersion: state.schemaVersion,
    };
    let usage = approximateJsonBytes(logical);
    for (const record of state.blobs.values())
        usage += record.blob.size;
    return usage;
}
function sameOperation(input, existing) {
    return input.kind === existing.kind
        && input.documentType === existing.documentType
        && input.documentId === existing.documentId
        && input.documentVersion === existing.documentVersion
        && JSON.stringify(input.payload) === JSON.stringify(existing.payload);
}
function enqueueOperation(state, input, clock, idSource) {
    const opId = input.opId ?? idSource.create();
    const existingSequence = state.operationSequences.get(opId);
    if (existingSequence !== undefined) {
        const existing = state.outbox.get(existingSequence);
        if (!existing || !sameOperation(input, existing))
            throw new StorageError('integrity-failed');
        return structuredClone(existing);
    }
    const operation = {
        opId,
        sequence: state.nextSequence,
        kind: input.kind,
        documentType: input.documentType,
        documentId: input.documentId,
        documentVersion: input.documentVersion,
        payload: structuredClone(input.payload),
        createdAt: input.createdAt ?? clock.now(),
    };
    assertOutboxOperation(operation);
    state.nextSequence += 1;
    state.outbox.set(operation.sequence, operation);
    state.operationSequences.set(operation.opId, operation.sequence);
    return structuredClone(operation);
}
class MemoryTransaction {
    state;
    mode;
    clock;
    idSource;
    emitOutbox;
    active = true;
    constructor(state, mode, clock, idSource, emitOutbox) {
        this.state = state;
        this.mode = mode;
        this.clock = clock;
        this.idSource = idSource;
        this.emitOutbox = emitOutbox;
    }
    finish() {
        this.active = false;
    }
    assertActive() {
        if (!this.active)
            throw new StorageError('transaction-failed', { detail: 'Transaction is no longer active' });
    }
    async get(type, id, options) {
        this.assertActive();
        const document = this.state.documents.get(documentKey(type, id));
        if (!document || (document.deletedAt !== null && !options?.includeDeleted))
            return null;
        return structuredClone(document);
    }
    async put(input, options = {}) {
        this.assertActive();
        const key = documentKey(input.type, input.id);
        assertJsonValue(input.data);
        if (options.opId && this.emitOutbox && this.mode === 'sync-ready') {
            const sequence = this.state.operationSequences.get(options.opId);
            const previousOperation = sequence === undefined ? undefined : this.state.outbox.get(sequence);
            if (previousOperation) {
                if (previousOperation.kind !== 'document-put'
                    || previousOperation.documentType !== input.type
                    || previousOperation.documentId !== input.id) {
                    throw new StorageError('integrity-failed');
                }
                if (JSON.stringify(previousOperation.payload.data) !== JSON.stringify(input.data)) {
                    throw new StorageError('integrity-failed');
                }
                return structuredClone(previousOperation.payload);
            }
        }
        const current = this.state.documents.get(key);
        checkExpectedVersion(current, options.expectedVersion);
        const document = createPutDocument(input, current, this.clock.now());
        this.state.documents.set(key, structuredClone(document));
        if (this.emitOutbox && this.mode === 'sync-ready') {
            const opId = options.opId ?? this.idSource.create();
            enqueueOperation(this.state, operationInputForDocument(document, 'document-put', opId), this.clock, this.idSource);
        }
        return structuredClone(document);
    }
    async delete(type, id, options = {}) {
        this.assertActive();
        const key = documentKey(type, id);
        if (options.opId && this.emitOutbox && this.mode === 'sync-ready') {
            const sequence = this.state.operationSequences.get(options.opId);
            const previousOperation = sequence === undefined ? undefined : this.state.outbox.get(sequence);
            if (previousOperation) {
                if (previousOperation.kind !== 'document-delete'
                    || previousOperation.documentType !== type
                    || previousOperation.documentId !== id) {
                    throw new StorageError('integrity-failed');
                }
                return structuredClone(previousOperation.payload);
            }
        }
        const current = this.state.documents.get(key);
        checkExpectedVersion(current, options.expectedVersion);
        if (!current)
            return null;
        const document = createDeletedDocument(current, this.clock.now());
        this.state.documents.set(key, structuredClone(document));
        if (this.emitOutbox && this.mode === 'sync-ready') {
            const opId = options.opId ?? this.idSource.create();
            enqueueOperation(this.state, operationInputForDocument(document, 'document-delete', opId), this.clock, this.idSource);
        }
        return structuredClone(document);
    }
    async list(query = {}) {
        this.assertActive();
        const { limit, offset } = normalizeQueryWindow(query.limit, query.offset);
        const matches = [...this.state.documents.values()]
            .filter((document) => (query.type === undefined || document.type === query.type)
            && (query.includeDeleted === true || document.deletedAt === null))
            .sort((left, right) => left.type.localeCompare(right.type) || left.id.localeCompare(right.id))
            .slice(offset, offset + limit);
        return structuredClone(matches);
    }
    query(query = {}) {
        return this.list(query);
    }
}
export class MemoryStorageBackend {
    mode;
    documents;
    outbox;
    blobs;
    state = initialState();
    closed = false;
    writeTail = Promise.resolve();
    clock;
    idSource;
    quotaBytes;
    constructor(options = {}) {
        this.mode = options.mode ?? 'sync-ready';
        this.clock = options.clock ?? defaultClock();
        this.idSource = options.idSource ?? defaultIdSource();
        this.quotaBytes = options.quotaBytes;
        if (this.quotaBytes !== undefined && (!Number.isSafeInteger(this.quotaBytes) || this.quotaBytes < 1)) {
            throw new StorageError('invalid-data', { detail: 'quotaBytes must be a positive integer' });
        }
        this.documents = this.createDocumentStore();
        this.outbox = this.createOutboxStore();
        this.blobs = this.createBlobStore();
    }
    assertOpen() {
        if (this.closed)
            throw new StorageError('closed');
    }
    async read(run) {
        this.assertOpen();
        await this.writeTail;
        this.assertOpen();
        return run(this.state);
    }
    async write(run) {
        this.assertOpen();
        const operation = this.writeTail.then(async () => {
            this.assertOpen();
            const draft = cloneState(this.state);
            const result = await run(draft);
            if (this.quotaBytes !== undefined && stateUsageBytes(draft) > this.quotaBytes) {
                throw new StorageError('quota-exceeded');
            }
            this.assertOpen();
            this.state = draft;
            return result;
        });
        this.writeTail = operation.then(() => undefined, () => undefined);
        return operation;
    }
    createDocumentStore() {
        const transact = async (run) => this.write(async (draft) => {
            const transaction = new MemoryTransaction(draft, this.mode, this.clock, this.idSource, true);
            try {
                return await run(transaction);
            }
            catch (error) {
                throw mapStorageError(error);
            }
            finally {
                transaction.finish();
            }
        });
        const list = async (query) => this.read(async (state) => {
            const transaction = new MemoryTransaction(state, this.mode, this.clock, this.idSource, false);
            try {
                return await transaction.list(query);
            }
            finally {
                transaction.finish();
            }
        });
        return {
            get: async (type, id, options) => this.read(async (state) => {
                const transaction = new MemoryTransaction(state, this.mode, this.clock, this.idSource, false);
                try {
                    return await transaction.get(type, id, options);
                }
                finally {
                    transaction.finish();
                }
            }),
            put: async (input, options) => transact((transaction) => transaction.put(input, options)),
            delete: (type, id, options) => transact((transaction) => transaction.delete(type, id, options)),
            list,
            query: list,
            count: (query = {}) => this.read((state) => [...state.documents.values()].filter((document) => ((query.type === undefined || document.type === query.type)
                && (query.includeDeleted === true || document.deletedAt === null))).length),
            transact,
        };
    }
    createOutboxStore() {
        return {
            enqueue: (input) => {
                if (this.mode === 'local-only')
                    return Promise.reject(new StorageError('unsupported', {
                        detail: 'Outbox is disabled in local-only mode',
                    }));
                return this.write(async (draft) => enqueueOperation(draft, input, this.clock, this.idSource));
            },
            list: (query = {}) => this.read((state) => {
                const { limit } = normalizeQueryWindow(query.limit, 0);
                const afterSequence = query.afterSequence ?? 0;
                if (!Number.isSafeInteger(afterSequence) || afterSequence < 0)
                    throw new StorageError('invalid-data');
                return structuredClone([...state.outbox.values()]
                    .filter((operation) => operation.sequence > afterSequence)
                    .sort((left, right) => left.sequence - right.sequence)
                    .slice(0, limit));
            }),
            acknowledge: (opIds) => this.write(async (draft) => {
                const uniqueIds = new Set(opIds);
                let deleted = 0;
                for (const opId of uniqueIds) {
                    const sequence = draft.operationSequences.get(opId);
                    if (sequence === undefined)
                        continue;
                    draft.operationSequences.delete(opId);
                    if (draft.outbox.delete(sequence))
                        deleted += 1;
                }
                return deleted;
            }),
            count: () => this.read((state) => state.outbox.size),
        };
    }
    createBlobStore() {
        const putPrepared = async (ref, input) => this.write(async (draft) => {
            assertBlobRef(ref);
            await assertBlobIntegrity(input, ref);
            draft.blobs.set(ref.id, { ref: structuredClone(ref), blob: input.slice(0, input.size, ref.mime) });
        });
        return {
            backendKind: 'memory',
            putPrepared,
            put: async (input, options = {}) => {
                const ref = await createBlobRef(input, options.mime ?? (input.type || 'application/octet-stream'), this.clock);
                await putPrepared(ref, input);
                return ref;
            },
            get: (ref) => this.read(async (state) => {
                assertBlobRef(ref);
                const record = state.blobs.get(ref.id);
                if (!record)
                    return null;
                await assertBlobIntegrity(record.blob, ref);
                return record.blob.slice(0, record.blob.size, record.ref.mime);
            }),
            delete: (ref) => this.write(async (draft) => {
                assertBlobRef(ref);
                draft.blobs.delete(ref.id);
            }),
            list: (options = {}) => this.read((state) => {
                const { limit, offset } = normalizeQueryWindow(options.limit, options.offset);
                return structuredClone([...state.blobs.values()]
                    .map(({ ref }) => ref)
                    .sort((left, right) => left.id.localeCompare(right.id))
                    .slice(offset, offset + limit));
            }),
        };
    }
    async getSchemaVersion() {
        return this.read((state) => state.schemaVersion);
    }
    async migrate(migrations) {
        return this.write(async (draft) => {
            const pending = pendingMigrations(draft.schemaVersion, migrations);
            const transaction = new MemoryTransaction(draft, this.mode, this.clock, this.idSource, false);
            try {
                for (const migration of pending) {
                    try {
                        await migration.migrate(transaction);
                    }
                    catch (error) {
                        throw new StorageError('migration-failed', {
                            cause: error,
                            detail: `Migration ${migration.version} (${migration.name}) failed`,
                        });
                    }
                    draft.schemaVersion = migration.version;
                }
                return draft.schemaVersion;
            }
            finally {
                transaction.finish();
            }
        });
    }
    async snapshot() {
        return this.read((state) => ({
            schemaVersion: state.schemaVersion,
            documents: structuredClone([...state.documents.values()]
                .sort((left, right) => left.type.localeCompare(right.type) || left.id.localeCompare(right.id))),
            outbox: structuredClone([...state.outbox.values()].sort((left, right) => left.sequence - right.sequence)),
        }));
    }
    async replace(snapshot) {
        await this.write(async (draft) => {
            draft.documents.clear();
            draft.outbox.clear();
            draft.operationSequences.clear();
            draft.schemaVersion = snapshot.schemaVersion;
            draft.nextSequence = 1;
            for (const document of snapshot.documents) {
                assertStoredDocument(document);
                draft.documents.set(documentKey(document.type, document.id), structuredClone(document));
            }
            for (const operation of snapshot.outbox) {
                assertOutboxOperation(operation);
                draft.outbox.set(operation.sequence, structuredClone(operation));
                draft.operationSequences.set(operation.opId, operation.sequence);
                draft.nextSequence = Math.max(draft.nextSequence, operation.sequence + 1);
            }
        });
    }
    async merge(snapshot) {
        await this.write(async (draft) => {
            draft.schemaVersion = Math.max(draft.schemaVersion, snapshot.schemaVersion);
            for (const incoming of snapshot.documents) {
                assertStoredDocument(incoming);
                const key = documentKey(incoming.type, incoming.id);
                const current = draft.documents.get(key);
                if (!current || incoming.version > current.version)
                    draft.documents.set(key, structuredClone(incoming));
            }
            for (const incoming of snapshot.outbox) {
                assertOutboxOperation(incoming);
                if (draft.operationSequences.has(incoming.opId))
                    continue;
                const operation = { ...structuredClone(incoming), sequence: draft.nextSequence };
                draft.nextSequence += 1;
                draft.outbox.set(operation.sequence, operation);
                draft.operationSequences.set(operation.opId, operation.sequence);
            }
        });
    }
    async estimateUsageBytes() {
        return this.read(stateUsageBytes);
    }
    close() {
        this.closed = true;
    }
}
//# sourceMappingURL=memory.js.map